import { useRillContext } from "../../../rill/RillReact/share/base";
import AdminToggle from "../components/AdminToggle";
import CoachLibraryDownload from "../components/CoachLibraryDownload";
import ResetButton from "../components/ResetButton";

/**
 * Admin: import a coach's program from the JSON Claude made from their
 * spreadsheet (msb-api script/import-program.prompt.md). Preview is a dry
 * run; a clean preview offers Import, which writes that same JSON.
 */
const ImportProgram = () => {
  const { locals } = useRillContext();
  const { importResult, query = {} } = locals;

  return (
    <main className="admin">
      <AdminToggle />

      <div className="admin-import">
        <header className="admin-import-header">
          <h2>Import Program</h2>
          <p>
            Download the coach's library for Claude, then upload the JSON Claude
            made: a program, or a library-only file. Preview checks it without
            writing anything.
          </p>
        </header>

        <CoachLibraryDownload
          defaultEmail={query.coachEmail}
          libraryPath="/admin/import-program/library"
          description="The coach's exercise library and primary lifts. Attach it to Claude when converting this coach's spreadsheets."
        />

        <form
          className="admin-import-form"
          // keep the chosen file after Preview (rill resets forms on submit)
          data-noreset
          method="POST"
          action="/actions/admin-panel/import-program"
          encType="multipart/form-data"
        >
          <div className="form-row">
            <div className="form-row-item">
              <label htmlFor="import-coach-email">Coach email</label>
              <input
                id="import-coach-email"
                type="email"
                name="coachEmail"
                placeholder="coach@example.com"
                required
                defaultValue={query.coachEmail}
              />
            </div>
            <div className="form-row-item">
              <label htmlFor="import-file">Program or library JSON</label>
              <input
                id="import-file"
                type="file"
                name="file"
                accept=".json,application/json"
                required
              />
            </div>
          </div>
          {/* apply travels in hidden inputs: rill's form serializer doesn't reliably send the clicked button */}
          <input type="hidden" name="apply" value="false" />
          <div className="admin-import-actions">
            <button type="submit" data-button-type="primary">
              Preview
            </button>
            <ResetButton path="/admin/import-program" />
          </div>
        </form>

        {importResult && <ImportResult result={importResult} />}
      </div>
    </main>
  );
};

const ImportResult = ({ result }) => {
  const { summary, errors = [], cycleId, program } = result;
  const isProgram = Boolean(summary?.blocks);

  return (
    <section className="admin-import-result">
      {cycleId && (
        <div className="admin-import-status" data-status="success">
          <strong>Imported</strong>
          <span>
            "{summary.name}" is now in {summary.coach.email}'s programs (cycle{" "}
            {cycleId}).
          </span>
        </div>
      )}
      {result.apply && !cycleId && !errors.length && (
        <div className="admin-import-status" data-status="success">
          <strong>Library updated</strong>
          <span>
            {summary.coach.email}'s exercise library has the changes below.
          </span>
        </div>
      )}

      {program && (
        <form
          className="admin-import-status"
          data-status="ready"
          method="POST"
          action="/actions/admin-panel/import-program"
          encType="multipart/form-data"
        >
          <div>
            <strong>{isProgram ? "Program" : "Library"} preview OK</strong>
            <span>
              {isProgram
                ? "Nothing has been written yet. Importing creates the program and adds the library changes below."
                : "Nothing has been written yet. Only the coach's exercise library changes — no program is created."}
            </span>
          </div>
          <input type="hidden" name="coachEmail" value={summary.coach.email} />
          <input type="hidden" name="program" value={program} />
          <input type="hidden" name="apply" value="true" />
          <button type="submit" data-button-type="primary">
            {isProgram ? "Import program into" : "Update library for"}{" "}
            {summary.coach.email}
          </button>
        </form>
      )}

      {errors.length > 0 && (
        <div className="admin-import-status" data-status="error">
          <strong>
            {errors.length} error{errors.length === 1 ? "" : "s"}
            {result.apply ? " — nothing written" : ""}
          </strong>
          <ul>
            {errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {summary && <ImportSummary summary={summary} />}
    </section>
  );
};

const ImportSummary = ({ summary }) => {
  // Defaults: the GUI and API deploy separately, so an older API may not send
  // every field yet (a missing libraryFills crashed this page on dev).
  const {
    libraryFills = [],
    primaryAdditions = [],
    repsPerSide = [],
  } = summary;
  const libraryAdditions = Object.entries(summary.libraryAdditions ?? {});
  const hasProgram = Boolean(summary.blocks);

  return (
    <>
      <h3>
        <span
          className="admin-import-kind"
          data-kind={hasProgram ? "program" : "library"}
        >
          {hasProgram ? "Program" : "Library only"}
        </span>
        {hasProgram ? summary.name : "Exercise library"}{" "}
        <span>· {summary.coach.email}</span>
      </h3>

      {hasProgram && <ProgramSummary summary={summary} />}

      <dl className="admin-import-details">
        {hasProgram && (
          <>
            <dt>Reps per side</dt>
            <dd>{repsPerSide.join(", ") || "None"}</dd>
          </>
        )}
        <dt>Added to the coach's library</dt>
        <dd>
          {libraryAdditions.length || primaryAdditions.length ? (
            <>
              {libraryAdditions.map(([primary, names]) => (
                <div key={primary}>
                  <em>{primary}:</em> {names.join(", ")}
                </div>
              ))}
              {primaryAdditions.length > 0 && (
                <div>
                  <em>primary lifts:</em> {primaryAdditions.join(", ")}
                </div>
              )}
            </>
          ) : (
            "Nothing — the coach already has every movement"
          )}
        </dd>
        <dt>Blanks filled in existing movements</dt>
        <dd>
          {libraryFills.length
            ? libraryFills.map((fill) => <div key={fill}>{fill}</div>)
            : "None"}
        </dd>
      </dl>
    </>
  );
};

const ProgramSummary = ({ summary }) => (
  <>
    <dl className="admin-import-stats">
      <div>
        <dt>Blocks</dt>
        <dd>{summary.blocks.length}</dd>
      </div>
      <div>
        <dt>Workouts</dt>
        <dd>{summary.sessions}</dd>
      </div>
      <div>
        <dt>Exercises</dt>
        <dd>{summary.exercises}</dd>
      </div>
      <div>
        <dt>Superset links</dt>
        <dd>{summary.supersets}</dd>
      </div>
      <div>
        <dt>Sections</dt>
        <dd>{summary.sections ?? 0}</dd>
      </div>
      <div>
        <dt>Workout notes</dt>
        <dd>{summary.workoutNotes ?? 0}</dd>
      </div>
    </dl>

    {/* biome-ignore lint/a11y/useValidAriaRole: table-wrapper used for structure */}
    <section role="table-wrapper" data-borders-inside /* NOSONAR */>
      {/* biome-ignore lint/a11y/useValidAriaRole: report-table used for structure */}
      <table role="report-table" /* NOSONAR */>
        {/* biome-ignore lint/a11y/useValidAriaRole: report-header used for structure */}
        <thead role="report-header" /* NOSONAR */>
          <tr>
            <th>Block</th>
            <th>Weeks</th>
            <th>Workouts / week</th>
            <th>Exercises</th>
          </tr>
        </thead>
        <tbody>
          {summary.blocks.map((block) => (
            <tr key={block.order}>
              <td>{block.name}</td>
              <td>{block.numberOfWeeks}</td>
              <td>{block.sessionsPerWeek}</td>
              <td>{block.exercises}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  </>
);

export default ImportProgram;
